import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, getDocs, deleteDoc, doc, orderBy, where, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    Briefcase, Trash2, Clock, Building2, Users, Search, MapPin, DollarSign
} from 'lucide-react';

function JobPostManagement({ user }) {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState('all');

    const loadJobs = useCallback(async () => {
        if (!user?.uid) return;
        try {
            setLoading(true);
            const q = query(
                collection(db, 'jobPostings'),
                orderBy('createdAt', 'desc')
            );
            const snapshot = await getDocs(q);
            const list = [];
            snapshot.forEach((docSnap) => {
                list.push({ id: docSnap.id, ...docSnap.data() });
            });
            setJobs(list);
        } catch (error) {
            console.error('구인공고 로드 실패:', error);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        loadJobs();
    }, [loadJobs]);

    const handleDelete = async (jobId) => {
        if (!window.confirm('정말 삭제하시겠습니까?')) return;
        try {
            await deleteDoc(doc(db, 'jobPostings', jobId));
            alert('삭제되었습니다.');
            loadJobs();
        } catch (error) {
            console.error('삭제 실패:', error);
            alert('삭제에 실패했습니다.');
        }
    };

    const handleStatusChange = async (jobId, newStatus) => {
        try {
            await updateDoc(doc(db, 'jobPostings', jobId), {
                status: newStatus,
                updatedAt: serverTimestamp()
            });
            loadJobs();
        } catch (error) {
            console.error('상태 변경 실패:', error);
            alert('상태 변경에 실패했습니다.');
        }
    };

    const filteredJobs = jobs.filter(job => {
        const matchesSearch = !searchTerm ||
            job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            job.companyName?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filter === 'all' || job.status === filter;
        return matchesSearch && matchesFilter;
    });

    const getStatusBadge = (status) => {
        const map = {
            active: { label: '활성', color: '#10b981', bg: '#d1fae5' },
            closed: { label: '마감', color: '#6b7280', bg: '#f3f4f6' }
        };
        const s = map[status] || { label: status || '알 수 없음', color: '#6b7280', bg: '#f3f4f6' };
        return (
            <span style={{ padding: '4px 10px', fontSize: '12px', fontWeight: '600', color: s.color, backgroundColor: s.bg, borderRadius: '6px' }}>
                {s.label}
            </span>
        );
    };

    if (loading) {
        return (
            <div style={styles.loading}>
                <div style={styles.spinner}></div>
                <p style={{ marginTop: '20px', color: '#64748b' }}>로딩 중...</p>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.titleSection}>
                    <Briefcase size={32} color="#6366f1" />
                    <div>
                        <h1 style={styles.title}>구인공고 관리</h1>
                        <p style={styles.subtitle}>등록된 구인공고를 관리합니다</p>
                    </div>
                </div>
            </div>

            <div style={styles.searchSection}>
                <div style={styles.searchBox}>
                    <Search size={20} style={styles.searchIcon} />
                    <input type="text" placeholder="공고 제목, 업체명으로 검색..." value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)} style={styles.searchInput} />
                </div>
                <div style={styles.filterButtons}>
                    {[['all', '전체'], ['active', '활성'], ['closed', '마감']].map(([key, label]) => (
                        <button key={key} style={filter === key ? styles.filterButtonActive : styles.filterButton}
                            onClick={() => setFilter(key)}>
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            <div style={styles.statsRow}>
                <div style={styles.statCard}>
                    <span style={styles.statLabel}>전체</span>
                    <span style={styles.statValue}>{jobs.length}</span>
                </div>
                <div style={styles.statCard}>
                    <span style={styles.statLabel}>활성</span>
                    <span style={{ ...styles.statValue, color: '#10b981' }}>{jobs.filter(j => j.status === 'active').length}</span>
                </div>
                <div style={styles.statCard}>
                    <span style={styles.statLabel}>마감</span>
                    <span style={{ ...styles.statValue, color: '#6b7280' }}>{jobs.filter(j => j.status === 'closed').length}</span>
                </div>
            </div>

            {filteredJobs.length === 0 ? (
                <div style={styles.emptyState}>
                    <Briefcase size={48} color="#cbd5e1" />
                    <p style={{ color: '#64748b', marginTop: '16px' }}>공고가 없습니다.</p>
                </div>
            ) : (
                <div style={styles.jobList}>
                    {filteredJobs.map(job => (
                        <div key={job.id} style={styles.jobCard}>
                            <div style={styles.jobCardTop}>
                                <div style={{ flex: 1 }}>
                                    <div style={styles.cardHeader}>
                                        <Building2 size={16} color="#64748b" />
                                        <span style={styles.cardCompany}>{job.companyName || '업체명 없음'}</span>
                                        {getStatusBadge(job.status)}
                                    </div>
                                    <h3 style={styles.cardTitle}>{job.title || '제목 없음'}</h3>
                                    <div style={styles.cardInfo}>
                                        {job.position && <span style={styles.cardTag}><Users size={14} />{job.position}</span>}
                                        {job.employmentType && <span style={styles.cardTag}><Clock size={14} />{job.employmentType}</span>}
                                        {job.salary && <span style={styles.cardTag}><DollarSign size={14} />{job.salary}</span>}
                                        {job.location && <span style={styles.cardTag}><MapPin size={14} />{job.location}</span>}
                                    </div>
                                </div>
                                <div style={styles.cardActions}>
                                    {job.status === 'active' && (
                                        <button style={styles.closeBtn} onClick={() => handleStatusChange(job.id, 'closed')} title="마감">
                                            <Clock size={16} />
                                        </button>
                                    )}
                                    {job.status === 'closed' && (
                                        <button style={styles.activateBtn} onClick={() => handleStatusChange(job.id, 'active')} title="활성화">
                                            <Briefcase size={16} />
                                        </button>
                                    )}
                                    <button style={styles.deleteBtn} onClick={() => handleDelete(job.id)} title="삭제">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

const styles = {
    container: { maxWidth: '1200px', margin: '0 auto', padding: '32px', backgroundColor: '#f8fafc', minHeight: '100vh' },
    loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px' },
    spinner: { width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTop: '3px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' },
    header: { marginBottom: '32px' },
    titleSection: { display: 'flex', alignItems: 'center', gap: '16px' },
    title: { margin: 0, fontSize: '28px', fontWeight: '700', color: '#0f172a' },
    subtitle: { margin: '4px 0 0 0', fontSize: '14px', color: '#64748b' },
    searchSection: { display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' },
    searchBox: { flex: 1, position: 'relative', minWidth: '250px' },
    searchIcon: { position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' },
    searchInput: { width: '100%', padding: '10px 14px 10px 44px', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', outline: 'none' },
    filterButtons: { display: 'flex', gap: '8px' },
    filterButton: { padding: '8px 16px', backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', fontWeight: '600', color: '#64748b', cursor: 'pointer' },
    filterButtonActive: { padding: '8px 16px', backgroundColor: '#6366f1', border: '2px solid #6366f1', borderRadius: '10px', fontSize: '13px', fontWeight: '600', color: 'white', cursor: 'pointer' },
    statsRow: { display: 'flex', gap: '16px', marginBottom: '24px' },
    statCard: { flex: 1, padding: '16px 20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center' },
    statLabel: { display: 'block', fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px' },
    statValue: { display: 'block', fontSize: '24px', fontWeight: '700', color: '#1e293b' },
    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', backgroundColor: 'white', borderRadius: '16px', border: '2px dashed #e2e8f0' },
    jobList: { display: 'flex', flexDirection: 'column', gap: '12px' },
    jobCard: { backgroundColor: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0' },
    jobCardTop: { display: 'flex', gap: '16px', alignItems: 'flex-start' },
    cardHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' },
    cardCompany: { fontSize: '13px', fontWeight: '600', color: '#475569' },
    cardTitle: { margin: '0 0 10px 0', fontSize: '17px', fontWeight: '700', color: '#1e293b' },
    cardInfo: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
    cardTag: { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#64748b' },
    cardActions: { display: 'flex', gap: '6px', flexShrink: 0 },
    closeBtn: { padding: '8px', backgroundColor: '#fef3c7', border: 'none', borderRadius: '8px', color: '#d97706', cursor: 'pointer' },
    activateBtn: { padding: '8px', backgroundColor: '#d1fae5', border: 'none', borderRadius: '8px', color: '#059669', cursor: 'pointer' },
    deleteBtn: { padding: '8px', backgroundColor: '#fee2e2', border: 'none', borderRadius: '8px', color: '#dc2626', cursor: 'pointer' }
};

export default JobPostManagement;
